import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PropsWithChildren } from 'react';

export interface ItemDraggableType {
  id: string | number;
  [key: string]: any;
}

interface ContainerProps {
  items: ItemDraggableType[];
  setItems?: React.Dispatch<React.SetStateAction<ItemDraggableType[]>>;
  onUpdate?: (values: ItemDraggableType[]) => void;
}

interface ItemProps {
  className?: string;
  key?: string;
  id: string;
}

const DraggrableContainer = ({
  children,
  items,
  setItems,
  onUpdate,
}: PropsWithChildren<ContainerProps>) => {
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!over || !over.id) return;

    if (active.id !== over.id) {
      if (setItems) {
        setItems((items: ItemDraggableType[]) => {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);

          return arrayMove(items, oldIndex, newIndex);
        });
      } else if (onUpdate) {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        onUpdate(arrayMove(items, oldIndex, newIndex));
      }
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      <SortableContext items={items}> {children}</SortableContext>
    </DndContext>
  );
};

const DraggableItem = (props: PropsWithChildren<ItemProps>) => {
  const { children, key, id } = props;

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  if (transform) {
    if (transform.scaleX) {
      transform.scaleX = 1;
    }
    if (transform.scaleY) {
      transform.scaleY = 1;
    }
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} key={key}>
      {children}
    </div>
  );
};

export { DraggrableContainer, DraggableItem };
